// One company's accounting policy: how its expenses are classified, and which
// real date its journals carry.
//
// These were constants in the connector until the platform learned that one
// customer's accounting answers are not every customer's. The split this
// screen exists to express:
//
//   KaziPeople owns  the accounting model, the journal engine, the lifecycle
//                    and which policies are supported at all
//   the customer     supplies how that model maps onto their own books
//
// So the SOURCES are fixed and the ORDER is theirs; the date BASES are fixed
// and the choice is theirs. Nothing here invents an account or a class.
//
// Advances per employee is selectable now that EmployeeAdvanceAccounts exists
// to bind people. Choosing it reveals that table, because an unbound person
// BLOCKS their next advance-funded claim rather than quietly falling back to a
// shared account — so the two controls belong next to each other.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { AlertCircle, ArrowDown, ArrowUp, Check, Loader2, ScrollText, X } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { getApiErrorMessage } from '@/lib/apiError';
import {
  getEntityAccountingConfig, setEntityAccountingConfig,
  type AccountingProvider, type DimensionSourceKind, type EntityPolicyView,
} from '@/services/accountingService';
import { useUnsavedGuard, usePendingGuard } from '@/features/shared/components/recovery/useFormGuards';

interface Props {
  provider?: AccountingProvider;
  /** Raised when the SAVED policy changes, so the page can show or hide the
   *  per-employee advance accounts. The saved value, not the draft: the table
   *  configures something only the stored policy actually uses. */
  onAdvanceModeChange?: (mode: string) => void;
  /** accounting:map — read-only without it. */
  canMap: boolean;
  systemName: string;
  /** The company whose policy this is. Shown, not guessed: these rules decide
   *  what reaches a real set of books. */
  legalEntityId: string;
  companyName?: string;
}

/** moveAt returns a copy with one entry moved. Pure, so the ordering rule is
 *  testable without rendering: the order IS the policy. */
export const moveAt = <T,>(list: T[], from: number, to: number): T[] => {
  if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

export const EntityAccountingPolicy: React.FC<Props> = ({
  provider = 'quickbooks', canMap, systemName, legalEntityId, companyName, onAdvanceModeChange,
}) => {
  const { t } = useTranslation();
  const [view, setView] = useState<EntityPolicyView | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  const [classOrder, setClassOrder] = useState<DimensionSourceKind[]>([]);
  const [locationOrder, setLocationOrder] = useState<DimensionSourceKind[]>([]);
  const [expenseDate, setExpenseDate] = useState('');
  const [payrollDate, setPayrollDate] = useState('');
  const [advanceMode, setAdvanceMode] = useState('');
  const [closedPeriod, setClosedPeriod] = useState('');
  const [contractorMode, setContractorMode] = useState('bill');
  // Policies in use but not yet agreed. Carried through every save so an
  // unrelated change cannot quietly promote one to decided.
  const [provisional, setProvisional] = useState<string[]>([]);

  const sourceLabel = useCallback((kind: string) => ({
    expense_category: t('accounting.sourceCategory', { defaultValue: 'Expense category' }),
    department: t('accounting.sourceDepartment', { defaultValue: "Employee's department" }),
    branch: t('accounting.sourceBranch', { defaultValue: 'Branch chosen on the claim' }),
    legal_entity: t('accounting.sourceEntity', { defaultValue: 'The company itself' }),
  }[kind] ?? kind), [t]);

  const advanceLabel = useCallback((mode: string) => ({
    none: t('accounting.advanceNone', { defaultValue: 'Do not post advances separately' }),
    control_account: t('accounting.advanceControl', { defaultValue: 'One shared staff-advance account' }),
    employee_subaccount: t('accounting.advanceEmployee', { defaultValue: "Each employee's own account" }),
  }[mode] ?? mode), [t]);

  const closedPeriodLabel = useCallback((p: string) => ({
    flag_for_redate: t('accounting.closedRedate', {
      defaultValue: 'Hold it, and offer to move it into an open period',
    }),
    block: t('accounting.closedBlock', { defaultValue: 'Refuse it and leave the date alone' }),
  }[p] ?? p), [t]);

  const dateLabel = useCallback((basis: string) => ({
    transaction: t('accounting.dateTransaction', { defaultValue: 'The latest receipt date on the claim' }),
    approval: t('accounting.dateApproval', { defaultValue: 'The date the claim was approved' }),
    settlement: t('accounting.dateSettlement', { defaultValue: 'The date the employee was paid' }),
    period_month_start: t('accounting.datePeriodMonthStart', { defaultValue: 'The first day of the payroll month' }),
    period_end: t('accounting.datePeriodEnd', { defaultValue: 'The last day of the payroll period' }),
  }[basis] ?? basis), [t]);

  /** NeedsConfirmation marks a policy that is in use but not agreed, with the
   *  one action that resolves it. Deliberately beside the control rather than
   *  a banner at the top: the question is "is THIS right?", and a list of
   *  field names somewhere else is not answerable. */
  const NeedsConfirmation: React.FC<{ field: string }> = ({ field }) => {
    if (!provisional.includes(field)) return null;
    return (
      <span className="inline-flex flex-wrap items-center gap-1.5">
        <span className="inline-flex items-center gap-1 rounded-md border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-[11px] text-foreground">
          <AlertCircle className="h-3 w-3" aria-hidden />
          {t('accounting.policyNeedsConfirming', { defaultValue: 'Not confirmed yet' })}
        </span>
        {canMap && (
          <Button
            size="sm" variant="ghost" className="h-6 px-1.5 text-[11px]"
            disabled={saving}
            onClick={() => setProvisional((p) => p.filter((f) => f !== field))}
          >
            <Check className="mr-1 h-3 w-3" aria-hidden />
            {t('accounting.policyConfirm', { defaultValue: 'Confirm' })}
          </Button>
        )}
      </span>
    );
  };

  const load = useCallback(async () => {
    if (!legalEntityId) return;
    setLoading(true);
    try {
      const v = await getEntityAccountingConfig(provider, legalEntityId);
      setView(v);
      setClassOrder(v.config?.expense_class_source ?? []);
      setLocationOrder(v.config?.expense_location_source ?? []);
      setExpenseDate(v.config?.expense_date_basis ?? '');
      setPayrollDate(v.config?.payroll_date_basis ?? '');
      setAdvanceMode(v.config?.advance_mode ?? '');
      setClosedPeriod(v.config?.closed_period_policy ?? '');
      setContractorMode(v.config?.contractor_posting_mode || 'bill');
      setProvisional(v.config?.provisional ?? []);
      onAdvanceModeChange?.(v.config?.advance_mode ?? '');
      setUnavailable(false);
    } catch {
      // A settings section, not the screen itself: it hides rather than
      // pushing an error at someone who came to map accounts.
      setUnavailable(true);
    } finally {
      setLoading(false);
    }
    // onAdvanceModeChange is a notifier, not an input: including it would
    // reload the policy whenever the parent re-rendered with a new closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, legalEntityId]);

  useEffect(() => { void load(); }, [load]);

  const supports = useCallback(
    (dim: string) => (view?.options.supported_dimensions ?? []).includes(dim),
    [view],
  );

  // Only what this policy has not already used. A source cannot decide twice,
  // and offering it again would produce a save the server refuses.
  const unused = useCallback(
    (order: DimensionSourceKind[]) => (view?.options.dimension_sources ?? []).filter((k) => !order.includes(k)),
    [view],
  );

  const dirty = useMemo(() => {
    const c = view?.config;
    if (!c) {
      return classOrder.length > 0 || locationOrder.length > 0
        || !!expenseDate || !!payrollDate || !!advanceMode || !!closedPeriod;
    }
    return (
      c.expense_class_source.join(',') !== classOrder.join(',') ||
      c.expense_location_source.join(',') !== locationOrder.join(',') ||
      c.expense_date_basis !== expenseDate ||
      c.payroll_date_basis !== payrollDate ||
      c.advance_mode !== advanceMode ||
      c.closed_period_policy !== closedPeriod ||
      (c.contractor_posting_mode || 'bill') !== contractorMode ||
      // ?? [] because a response from before this field existed would
      // otherwise throw here and white-screen the settings page.
      [...(c.provisional ?? [])].sort().join(',') !== [...provisional].sort().join(',')
    );
  }, [view, classOrder, locationOrder, expenseDate, payrollDate, advanceMode, closedPeriod, contractorMode, provisional]);

  // Reload guard. `dirty` is computed from the loaded config versus the live
  // fields, so it stays true when a save fails — which is exactly when the
  // hold has to survive. `saving` is cleared in the finally below.
  useUnsavedGuard(dirty, `entity-accounting:${legalEntityId}`, companyName
    ? `accounting policy for ${companyName}` : 'accounting policy');
  usePendingGuard(saving, `entity-accounting:${legalEntityId}`, companyName
    ? `accounting policy for ${companyName}` : 'accounting policy');

  const save = async () => {
    setSaving(true);
    try {
      const next: EntityPolicyView = await setEntityAccountingConfig(provider, legalEntityId, {
        expense_class_source: classOrder,
        expense_location_source: locationOrder,
        expense_date_basis: expenseDate,
        payroll_date_basis: payrollDate,
        advance_mode: advanceMode,
        closed_period_policy: closedPeriod,
        contractor_posting_mode: contractorMode,
        provisional,
      });
      setView(next);
      setProvisional(next.config?.provisional ?? []);
      onAdvanceModeChange?.(next.config?.advance_mode ?? '');
      toast.success(t('accounting.policySaved', {
        defaultValue: 'Saved. New journals for this company follow these rules.',
      }));
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('accounting.policySaveFailed', {
        defaultValue: 'Could not save the accounting policy.',
      })));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          {t('accounting.policyLoading', { defaultValue: 'Reading this company’s accounting policy…' })}
        </CardContent>
      </Card>
    );
  }
  if (unavailable || !view) return null;

  const orderEditor = (
    dimension: 'class' | 'location',
    order: DimensionSourceKind[],
    setOrder: (next: DimensionSourceKind[]) => void,
  ) => (
    <div className="space-y-2">
      {order.length === 0 && (
        <p className="rounded-md border border-dashed border-border px-3 py-2 text-xs text-muted-foreground">
          {t('accounting.policyNoSources', {
            defaultValue: 'No sources — this company does not set a {{dimension}} on expenses, and journals post without one.',
            dimension,
          })}
        </p>
      )}
      <ol className="space-y-1">
        {order.map((kind, i) => (
          <li key={kind} className="flex items-center gap-2 rounded-md border border-border px-2 py-1.5">
            <span className="w-4 text-xs text-muted-foreground">{i + 1}</span>
            <span className="flex-1 text-xs text-foreground">{sourceLabel(kind)}</span>
            <Button
              size="sm" variant="ghost" className="h-6 w-6 p-0"
              disabled={!canMap || saving || i === 0}
              aria-label={t('accounting.policyMoveUp', { defaultValue: 'Move {{name}} earlier', name: sourceLabel(kind) })}
              onClick={() => setOrder(moveAt(order, i, i - 1))}
            >
              <ArrowUp className="h-3.5 w-3.5" aria-hidden />
            </Button>
            <Button
              size="sm" variant="ghost" className="h-6 w-6 p-0"
              disabled={!canMap || saving || i === order.length - 1}
              aria-label={t('accounting.policyMoveDown', { defaultValue: 'Move {{name}} later', name: sourceLabel(kind) })}
              onClick={() => setOrder(moveAt(order, i, i + 1))}
            >
              <ArrowDown className="h-3.5 w-3.5" aria-hidden />
            </Button>
            <Button
              size="sm" variant="ghost" className="h-6 w-6 p-0"
              disabled={!canMap || saving}
              aria-label={t('accounting.policyRemove', { defaultValue: 'Remove {{name}}', name: sourceLabel(kind) })}
              onClick={() => setOrder(order.filter((k) => k !== kind))}
            >
              <X className="h-3.5 w-3.5" aria-hidden />
            </Button>
          </li>
        ))}
      </ol>
      {unused(order).length > 0 && canMap && (
        <select
          aria-label={t('accounting.policyAddSource', { defaultValue: 'Add a source for {{dimension}}', dimension })}
          className="h-8 w-full min-w-[14rem] rounded-md border border-border bg-background px-2 text-xs text-foreground"
          value=""
          disabled={saving}
          onChange={(e) => {
            const kind = e.target.value as DimensionSourceKind;
            if (kind) setOrder([...order, kind]);
          }}
        >
          <option value="">{t('accounting.policyAddSourcePrompt', { defaultValue: 'Add a source…' })}</option>
          {unused(order).map((k) => (
            <option key={k} value={k}>{sourceLabel(k)}</option>
          ))}
        </select>
      )}
    </div>
  );

  return (
    <Card>
      <CardContent className="space-y-5 py-5">
        <div className="flex items-start gap-3">
          <ScrollText className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
          <div className="space-y-1">
            <h3 className="text-sm font-medium text-foreground">
              {t('accounting.policyTitle', { defaultValue: 'Accounting policy' })}
            </h3>
            <p className="text-xs text-muted-foreground">
              {t('accounting.policyHelp', {
                defaultValue:
                  'How {{company}}’s journals are written: what decides a class, and which real date a journal carries. Each company sets its own — nothing is inherited from another.',
                company: companyName || view.company_name
                  || t('accounting.policyThisCompany', { defaultValue: 'this company' }),
              })}
            </p>
          </div>
        </div>

        {!view.configured && (
          // Named plainly, because posting refuses until this is answered and
          // the person pressing Post sees the same wording.
          <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-foreground">
            {t('accounting.policyUnset', {
              defaultValue:
                'This company has not chosen its accounting policies yet, so nothing can be posted to {{system}} for it. Choose below and save.',
              system: systemName,
            })}
          </p>
        )}

        {supports('class') && (
          <section className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <h4 className="text-xs font-medium text-foreground">
                {t('accounting.policyClassTitle', { defaultValue: 'What decides an expense’s class' })}
              </h4>
              <NeedsConfirmation field="expense_class_source" />
            </div>
            <p className="text-xs text-muted-foreground">
              {t('accounting.policyClassHelp', {
                defaultValue: 'The first source with a mapping wins. Remove them all if this company does not use classes on expenses.',
              })}
            </p>
            {orderEditor('class', classOrder, setClassOrder)}
          </section>
        )}

        {supports('location') && (
          <section className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <h4 className="text-xs font-medium text-foreground">
                {t('accounting.policyLocationTitle', { defaultValue: 'What decides an expense’s location' })}
              </h4>
              <NeedsConfirmation field="expense_location_source" />
            </div>
            {orderEditor('location', locationOrder, setLocationOrder)}
          </section>
        )}

        <section className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <label htmlFor="expense-date-basis" className="text-xs font-medium text-foreground">
                {t('accounting.policyExpenseDate', { defaultValue: 'An expense journal is dated' })}
              </label>
              <NeedsConfirmation field="expense_date_basis" />
            </div>
            <select
              id="expense-date-basis"
              className="h-8 w-full rounded-md border border-border bg-background px-2 text-xs text-foreground"
              value={expenseDate}
              disabled={!canMap || saving}
              onChange={(e) => setExpenseDate(e.target.value)}
            >
              <option value="">{t('accounting.policyChoose', { defaultValue: 'Choose…' })}</option>
              {view.options.expense_date_bases.map((b) => (
                <option key={b} value={b}>{dateLabel(b)}</option>
              ))}
            </select>
            {expenseDate === 'settlement' && (
              // Said before they save, not after a posting is refused.
              <p className="text-xs text-muted-foreground">
                {t('accounting.policySettlementNote', {
                  defaultValue: 'A claim that has not been paid yet cannot be posted until it is.',
                })}
              </p>
            )}
          </div>
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <label htmlFor="payroll-date-basis" className="text-xs font-medium text-foreground">
                {t('accounting.policyPayrollDate', { defaultValue: 'A payroll journal is dated' })}
              </label>
              <NeedsConfirmation field="payroll_date_basis" />
            </div>
            <select
              id="payroll-date-basis"
              className="h-8 w-full rounded-md border border-border bg-background px-2 text-xs text-foreground"
              value={payrollDate}
              disabled={!canMap || saving}
              onChange={(e) => setPayrollDate(e.target.value)}
            >
              <option value="">{t('accounting.policyChoose', { defaultValue: 'Choose…' })}</option>
              {view.options.payroll_date_bases.map((b) => (
                <option key={b} value={b}>{dateLabel(b)}</option>
              ))}
            </select>
          </div>
        </section>

        <section className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <label htmlFor="advance-mode" className="text-xs font-medium text-foreground">
              {t('accounting.policyAdvance', { defaultValue: 'When an advance has already funded a claim' })}
            </label>
            <NeedsConfirmation field="advance_mode" />
          </div>
          <p className="text-xs text-muted-foreground">
            {t('accounting.policyAdvanceHelp', {
              defaultValue:
                'The recovered part credits this account, so the employee is only shown as owed what is still outstanding. Leave it unset and an advance-funded claim will not post at all, rather than overstating what you owe.',
            })}
          </p>
          <select
            id="advance-mode"
            className="h-8 w-full rounded-md border border-border bg-background px-2 text-xs text-foreground sm:w-1/2"
            value={advanceMode}
            disabled={!canMap || saving}
            onChange={(e) => setAdvanceMode(e.target.value)}
          >
            <option value="">{t('accounting.policyChoose', { defaultValue: 'Choose…' })}</option>
            {(view.options.advance_modes ?? []).map((m) => (
              <option key={m} value={m}>{advanceLabel(m)}</option>
            ))}
          </select>
          {advanceMode === 'employee_subaccount' && (
            // Said before they save, because the accounts have to be bound
            // below and an unbound person blocks their next claim.
            <p className="text-xs text-muted-foreground">
              {t('accounting.advanceEmployeeNote', {
                defaultValue: 'Each person needs their own account bound below before an advance-funded claim of theirs can post.',
              })}
            </p>
          )}
        </section>

        <section className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <label htmlFor="closed-period" className="text-xs font-medium text-foreground">
              {t('accounting.policyClosedPeriod', { defaultValue: 'When the ledger period is already closed' })}
            </label>
            <NeedsConfirmation field="closed_period_policy" />
          </div>
          <p className="text-xs text-muted-foreground">
            {t('accounting.policyClosedPeriodHelp', {
              defaultValue:
                'A correct journal against a shut book. Neither choice guesses which period to use instead — we never move a journal into a month you did not pick.',
            })}
          </p>
          <select
            id="closed-period"
            className="h-8 w-full rounded-md border border-border bg-background px-2 text-xs text-foreground sm:w-1/2"
            value={closedPeriod}
            disabled={!canMap || saving}
            onChange={(e) => setClosedPeriod(e.target.value)}
          >
            <option value="">{t('accounting.policyChoose', { defaultValue: 'Choose…' })}</option>
            {(view.options.closed_period_policies ?? []).map((p) => (
              <option key={p} value={p}>{closedPeriodLabel(p)}</option>
            ))}
          </select>
        </section>

        <section className="space-y-1">
          <label htmlFor="contractor-mode" className="text-xs font-medium text-foreground">
            {t('accounting.policyContractorMode', { defaultValue: 'How contractor payments post' })}
          </label>
          <p className="text-xs text-muted-foreground">
            {t('accounting.policyContractorModeHelp', {
              defaultValue:
                'Bills: a Bill per contractor (gross) plus a vendor credit for withholding at approval, and a bill payment from the bank when the payout settles. Journal: one balanced journal per run, and a payment journal when paid. A run already posted keeps the way it was posted; a change applies to runs not posted yet.',
            })}
          </p>
          <select
            id="contractor-mode"
            data-testid="contractor-mode"
            className="h-8 w-full rounded-md border border-border bg-background px-2 text-xs text-foreground sm:w-1/2"
            value={contractorMode}
            disabled={!canMap || saving}
            onChange={(e) => setContractorMode(e.target.value)}
          >
            <option value="bill">{t('accounting.contractorModeBill', { defaultValue: 'Bills and bill payments (default)' })}</option>
            <option value="journal">{t('accounting.contractorModeJournal', { defaultValue: 'Journal entries' })}</option>
          </select>
        </section>

        {(view.required_accounts ?? []).length > 0 && (
          <section className="space-y-2">
            <h4 className="text-xs font-medium text-foreground">
              {t('accounting.policyNeedsAccounts', { defaultValue: 'Accounts these settings need' })}
            </h4>
            <p className="text-xs text-muted-foreground">
              {t('accounting.policyNeedsAccountsHelp', {
                defaultValue:
                  'Which accounts are needed follows from the settings above — change the advance policy and this list changes. Map them in the chart below; nothing posts to an account that is not mapped.',
              })}
            </p>
            <ul className="space-y-1">
              {view.required_accounts.map((a, i) => (
                <li key={a.logical_code || `row-${i}`}
                  className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 rounded-md border border-border px-2 py-1.5">
                  {a.logical_code ? (
                    <span className={a.mapped ? 'text-xs text-foreground' : 'text-xs text-danger'}>
                      {a.mapped
                        ? t('accounting.policyAccountMapped', { defaultValue: 'Mapped' })
                        : t('accounting.policyAccountMissing', { defaultValue: 'Not mapped' })}
                    </span>
                  ) : (
                    // No logical role: the accounts come from the per-person
                    // bindings, so there is nothing here to tick.
                    <span className="text-xs text-muted-foreground">
                      {t('accounting.policyAccountPerPerson', { defaultValue: 'Set per person' })}
                    </span>
                  )}
                  <span className="text-xs text-foreground">{a.label}</span>
                  <span className="text-[11px] text-muted-foreground">— {a.why}</span>
                  {a.external_account_name && (
                    <span className="text-[11px] text-muted-foreground">({a.external_account_name})</span>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        {canMap && (
          <div className="flex items-center gap-3">
            <Button size="sm" onClick={() => void save()} disabled={saving || !dirty}>
              {saving && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" aria-hidden />}
              {t('accounting.policySave', { defaultValue: 'Save policy' })}
            </Button>
            <p className="text-xs text-muted-foreground">
              {t('accounting.policyAppliesNext', {
                defaultValue: 'Applies to journals posted from now on; nothing already posted changes.',
              })}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EntityAccountingPolicy;
